import {
  Breadcrumbs,
  Card,
  Typography,
  Grid,
  Input,
  Stack,
  FormLabel,
  Box,
  CircularProgress,
  Chip,
  Tabs,
  TabList,
  Tab,
  TabPanel,
} from '@mui/joy';
import { CreateEditTenantModal } from '@/components/Pages/Tenants/CreateEditTenantModal';
import { TenantActiveModal } from '@/components/Pages/Tenants/TenantActiveModal';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { pageTitle, usePageTitle } from '@/utils/pageTitle';
import { SuperAdminRoute } from '@/components/SuperAdminRoute';
import { UserTable } from '@/components/Pages/Users/UserTable';
import { CreateEditUserModal } from '@/components/Pages/Users/CreateEditUserModal';
import { useTenant } from '@/hooks/useTenants';
import { useState, useEffect } from 'react';
import { z } from 'zod';

export const Route = createFileRoute('/_protected/tenants/$id')({
  component: TenantDetail,
  head: () => pageTitle('Empresas'),
  validateSearch: z.object({
    tab: z.number().default(0),
  }),
});

function TenantDetail() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: tenant, isLoading } = useTenant(id);
  usePageTitle('Empresas', tenant?.name);
  const [selectedTab, setSelectedTab] = useState(tab);

  const handleTabChange = (value: number) => {
    setSelectedTab(value);
    navigate({ search: prev => ({ ...prev, tab: value }) });
  };

  useEffect(() => {
    setSelectedTab(tab);
  }, [tab]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!tenant) {
    return <div>Empresa no encontrada</div>;
  }

  return (
    <SuperAdminRoute>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Stack direction='row' justifyContent='space-between'>
          <Breadcrumbs size='sm'>
            <Link to='/tenants'>Empresas</Link>
            <Typography>{tenant?.name}</Typography>
          </Breadcrumbs>

          {tenant && (
            <Stack direction='row' gap={1} alignItems='center'>
              <Chip
                size='sm'
                color={tenant.active ? 'success' : 'danger'}
                variant='soft'
              >
                {tenant.active ? 'Activo' : 'Inactivo'}
              </Chip>
              <TenantActiveModal tenant={tenant} />
              <CreateEditTenantModal tenant={tenant} />
            </Stack>
          )}
        </Stack>

        <Tabs
          value={selectedTab}
          onChange={(_, value) => handleTabChange(Number(value))}
        >
          <TabList>
            <Tab>Información</Tab>
            <Tab>Usuarios</Tab>
          </TabList>

          <TabPanel value={0}>
            <Card>
              <Grid container spacing={{ xs: 1, sm: 2 }}>
                <Grid xs={12} md={6}>
                  <FormLabel>Nombre</FormLabel>
                  <Input
                    value={tenant?.name}
                    readOnly
                    variant='soft'
                    size='sm'
                    sx={{ backgroundColor: 'background.body' }}
                  />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormLabel>CUIT</FormLabel>
                  <Input
                    value={tenant?.taxId || ''}
                    readOnly
                    variant='soft'
                    size='sm'
                    sx={{ backgroundColor: 'background.body' }}
                  />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormLabel>Email</FormLabel>
                  <Input
                    value={tenant?.email || ''}
                    readOnly
                    variant='soft'
                    size='sm'
                    type='email'
                    sx={{ backgroundColor: 'background.body' }}
                  />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormLabel>Nombre Fantasía</FormLabel>
                  <Input
                    value={tenant?.fantasyName || ''}
                    readOnly
                    variant='soft'
                    size='sm'
                    sx={{ backgroundColor: 'background.body' }}
                  />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormLabel>Dirección</FormLabel>
                  <Input
                    value={tenant?.address || ''}
                    readOnly
                    variant='soft'
                    size='sm'
                    sx={{ backgroundColor: 'background.body' }}
                  />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormLabel>Teléfono</FormLabel>
                  <Input
                    value={tenant?.phone || ''}
                    readOnly
                    variant='soft'
                    size='sm'
                    sx={{ backgroundColor: 'background.body' }}
                  />
                </Grid>
              </Grid>
            </Card>
          </TabPanel>

          <TabPanel value={1}>
            <Card>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography level='h4'>Usuarios</Typography>
                <CreateEditUserModal
                  tenantId={tenant.id}
                  disabled={!tenant.active}
                />
              </Box>
              <UserTable
                tenantId={tenant.id}
                emptyMessage='No hay usuarios para esta empresa'
                errorTitle='Error al cargar los usuarios de esta empresa'
              />
            </Card>
          </TabPanel>
        </Tabs>
      </Box>
    </SuperAdminRoute>
  );
}
